import React from "react";
import { Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Target, Compass, Sparkles, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="About WriterSpark | Our Mission for Writing Skill Development"
        description="WriterSpark is an interactive writing improvement platform built for middle school, high school, AP, SAT, and ESL students. Learn why we built a coach that teaches, not corrects."
        path="/about"
        keywords="about WriterSpark, writing improvement platform, writing tutor for students, EdTech writing"
      />
      <PublicHeader />

      <section className="max-w-4xl mx-auto px-6 py-16">
        <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">About WriterSpark</span>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight" style={{fontFamily:'Outfit'}}>
          Writing is thinking made visible. We help students think — and write — better.
        </h1>
        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
          WriterSpark is an interactive writing improvement platform built for middle-schoolers, high-schoolers, AP and SAT students, ESL learners, and the parents and teachers who support them.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-12 grid md:grid-cols-3 gap-6">
        {[
          {icon: Target, title: "Our mission", body: "Replace ‘fix-my-grammar’ tools with a coach that actually teaches — because skill, not output, is what students need."},
          {icon: Compass, title: "Our approach", body: "Short, daily practice. Real rubric feedback. Coach-style guidance that asks first and answers last."},
          {icon: Sparkles, title: "Our craft", body: "Built with love for the science of writing instruction — and the warmth a young writer deserves on a hard day."},
        ].map((b, i) => {
          const Icon = b.icon;
          return (
            <div key={i} data-testid={`about-block-${i}`} className="tactile-card p-7">
              <div className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-700 grid place-items-center mb-4"><Icon size={22}/></div>
              <h3 className="text-xl font-bold mb-2" style={{fontFamily:'Outfit'}}>{b.title}</h3>
              <p className="text-slate-600 leading-relaxed">{b.body}</p>
            </div>
          );
        })}
      </section>

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-20 space-y-8 text-slate-700 leading-relaxed">
          <h2 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Why this exists</h2>
          <p>
            Generative AI changed what students <em>can</em> turn in. It hasn't changed what they <strong>need to learn</strong>. The ability to clarify a thought, defend a claim, and revise toward precision is more valuable now, not less.
          </p>
          <p>
            But the existing tools are split: grammar checkers correct without teaching; chatbots write without coaching. Neither builds a writer.
          </p>
          <p>
            WriterSpark is the third option — a platform that <strong>teaches the move</strong>: how to break down a prompt, organize evidence, address a counterargument, sharpen a thesis, and revise with intention. Then it gets out of the way and lets the student write.
          </p>
          <h2 className="text-3xl font-bold pt-6" style={{fontFamily:'Outfit'}}>How we're different</h2>
          <ul className="space-y-3 list-disc pl-6">
            <li>We <strong>never</strong> hand students a finished draft. The coach asks, models, and steps back.</li>
            <li>Feedback is <strong>rubric-based</strong> — Ideas, Organization, Voice, Word Choice, Conventions — not a single ‘rewrite’ button.</li>
            <li>Tone and rigor <strong>adapt by grade band</strong> so a 4th-grader and an AP student each get a coach that meets them where they are.</li>
            <li>We make practice <strong>visible</strong>: streaks, badges, and rubric-trend graphs that turn improvement into momentum.</li>
          </ul>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h2 className="text-3xl font-bold mb-4" style={{fontFamily:'Outfit'}}>Ready to write better?</h2>
        <Link to="/register" data-testid="about-cta" className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-7 py-3.5 tactile-shadow">
          Create a free account <ArrowRight size={18}/>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
